"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import dynamic from "next/dynamic";
import { useMatch } from "fansunited-data-layer/client";
import { Tooltip } from "fansunited-sports-ui";
import { BrandedMatchHeader } from "@/components/branded/headers/BrandedMatchHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { PageNavigation } from "@/components/layout/PageNavigation";
import { useLabels, useLocale } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useCompetitionProfileOptional } from "@/features/competition-profiles/CompetitionProfileProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { AdSlot } from "@/features/ads/AdSlot";
import { useMatchPageData } from "../context/MatchPageDataContext";
import { formatPosition } from "../utils/formatPosition";
import { getRelativeDate } from "../utils/getRelativeDate";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { computeScoreDisplay } from "@/utilities/formatting/scoreDisplay";
import { MatchCompetitionInfo } from "@/components/matches/MatchCompetitionInfo";
import { MatchBroadcastersRow } from "@/features/tv-listings/frontend/MatchBroadcastersRow";

// Dynamically import tabs — only the active tab's client JS is loaded
const OddsTab = dynamic(() => import("../tabs/OddsTab").then(m => ({
  default: m.OddsTab
})));
const EventsTab = dynamic(() => import("../tabs/EventsTab").then(m => ({
  default: m.EventsTab
})));
const FormTab = dynamic(() => import("../tabs/FormTab").then(m => ({
  default: m.FormTab
})));
const StatsTab = dynamic(() => import("../tabs/StatsTab").then(m => ({
  default: m.StatsTab
})));
const StandingsTab = dynamic(() => import("../tabs/StandingsTab").then(m => ({
  default: m.StandingsTab
})));
const NewsTab = dynamic(() => import("../tabs/NewsTab").then(m => ({
  default: m.NewsTab
})));
const H2HTab = dynamic(() => import("../tabs/H2HTab").then(m => ({
  default: m.H2HTab
})));
const LineupsTab = dynamic(() => import("../tabs/LineupsTab").then(m => ({
  default: m.LineupsTab
})));
type MatchTab = "events" | "betting" | "lineups" | "stats" | "form" | "standings" | "h2h" | "news";
export function Match(t0) {
  const $ = _c(237);
  const {
    headerColors,
    breadcrumbs
  } = t0;
  const labels = useLabels();
  const locale = useLocale();
  const nav = useNavigation();
  const {
    getTeamStanding
  } = useMatch();
  const competitionProfile = useCompetitionProfileOptional();
  const {
    hasOdds
  } = useTenantSettings();
  const {
    match,
    homeTeam,
    awayTeam,
    homeTeamNativeId,
    awayTeamNativeId,
    isLive,
    isFinished,
    hasLineups,
    hasEvents,
    winner,
    oddsMap,
    oddsLoading,
    refetchOdds,
    tvListings
  } = useMatchPageData();
  let t1;
  if ($[0] !== hasEvents || $[1] !== hasLineups || $[2] !== hasOdds || $[3] !== isFinished || $[4] !== isLive) {
    t1 = () => {
      if (isFinished) {
        return hasEvents ? "events" : "stats";
      }
      if (hasOdds) {
        return "betting";
      }
      if (isLive) {
        return hasEvents ? "events" : hasLineups ? "lineups" : "form";
      }
      return hasLineups ? "lineups" : "form";
    };
    $[0] = hasEvents;
    $[1] = hasLineups;
    $[2] = hasOdds;
    $[3] = isFinished;
    $[4] = isLive;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const getDefaultTab = t1;
  const [activeTab, setActiveTab] = useState(getDefaultTab);
  let tabs;
  if ($[6] !== competitionProfile?.features.matchStandings || $[7] !== hasEvents || $[8] !== hasLineups || $[9] !== hasOdds || $[10] !== isFinished || $[11] !== isLive || $[12] !== labels.matchPage.tabs) {
    tabs = [];
    const tabLabels = labels.matchPage.tabs;
    if (isFinished) {
      if (hasEvents) {
        let t2;
        if ($[14] !== tabLabels.events) {
          t2 = {
            id: "events",
            label: tabLabels.events
          };
          $[14] = tabLabels.events;
          $[15] = t2;
        } else {
          t2 = $[15];
        }
        tabs.push(t2);
      }
      if (hasLineups) {
        let t2;
        if ($[16] !== tabLabels.lineups) {
          t2 = {
            id: "lineups",
            label: tabLabels.lineups
          };
          $[16] = tabLabels.lineups;
          $[17] = t2;
        } else {
          t2 = $[17];
        }
        tabs.push(t2);
      }
      let t2;
      if ($[18] !== tabLabels.stats) {
        t2 = {
          id: "stats",
          label: tabLabels.stats
        };
        $[18] = tabLabels.stats;
        $[19] = t2;
      } else {
        t2 = $[19];
      }
      tabs.push(t2);
      if (competitionProfile?.features.matchStandings !== false) {
        let t3;
        if ($[20] !== tabLabels.standings) {
          t3 = {
            id: "standings",
            label: tabLabels.standings
          };
          $[20] = tabLabels.standings;
          $[21] = t3;
        } else {
          t3 = $[21];
        }
        tabs.push(t3);
      }
    } else {
      if (isLive) {
        if (hasOdds) {
          let t2;
          if ($[22] !== tabLabels.odds) {
            t2 = {
              id: "betting",
              label: tabLabels.odds
            };
            $[22] = tabLabels.odds;
            $[23] = t2;
          } else {
            t2 = $[23];
          }
          tabs.push(t2);
        }
        if (hasEvents) {
          let t2;
          if ($[24] !== tabLabels.events) {
            t2 = {
              id: "events",
              label: tabLabels.events
            };
            $[24] = tabLabels.events;
            $[25] = t2;
          } else {
            t2 = $[25];
          }
          tabs.push(t2);
        }
        if (hasLineups) {
          let t2;
          if ($[26] !== tabLabels.lineups) {
            t2 = {
              id: "lineups",
              label: tabLabels.lineups
            };
            $[26] = tabLabels.lineups;
            $[27] = t2;
          } else {
            t2 = $[27];
          }
          tabs.push(t2);
        }
        let t2;
        if ($[28] !== tabLabels.form) {
          t2 = {
            id: "form",
            label: tabLabels.form
          };
          $[28] = tabLabels.form;
          $[29] = t2;
        } else {
          t2 = $[29];
        }
        tabs.push(t2);
        if (competitionProfile?.features.matchStandings !== false) {
          let t3;
          if ($[30] !== tabLabels.standings) {
            t3 = {
              id: "standings",
              label: tabLabels.standings
            };
            $[30] = tabLabels.standings;
            $[31] = t3;
          } else {
            t3 = $[31];
          }
          tabs.push(t3);
        }
        let t3;
        if ($[32] !== tabLabels.stats) {
          t3 = {
            id: "stats",
            label: tabLabels.stats
          };
          $[32] = tabLabels.stats;
          $[33] = t3;
        } else {
          t3 = $[33];
        }
        tabs.push(t3);
      } else {
        if (hasOdds) {
          let t2;
          if ($[34] !== tabLabels.odds) {
            t2 = {
              id: "betting",
              label: tabLabels.odds
            };
            $[34] = tabLabels.odds;
            $[35] = t2;
          } else {
            t2 = $[35];
          }
          tabs.push(t2);
        }
        if (hasLineups) {
          let t2;
          if ($[36] !== tabLabels.lineups) {
            t2 = {
              id: "lineups",
              label: tabLabels.lineups
            };
            $[36] = tabLabels.lineups;
            $[37] = t2;
          } else {
            t2 = $[37];
          }
          tabs.push(t2);
        }
        let t2;
        if ($[38] !== tabLabels.form) {
          t2 = {
            id: "form",
            label: tabLabels.form
          };
          $[38] = tabLabels.form;
          $[39] = t2;
        } else {
          t2 = $[39];
        }
        tabs.push(t2);
        if (competitionProfile?.features.matchStandings !== false) {
          let t3;
          if ($[40] !== tabLabels.standings) {
            t3 = {
              id: "standings",
              label: tabLabels.standings
            };
            $[40] = tabLabels.standings;
            $[41] = t3;
          } else {
            t3 = $[41];
          }
          tabs.push(t3);
        }
      }
    }
    let t2;
    if ($[42] !== tabLabels.h2h) {
      t2 = {
        id: "h2h",
        label: tabLabels.h2h
      };
      $[42] = tabLabels.h2h;
      $[43] = t2;
    } else {
      t2 = $[43];
    }
    let t3;
    if ($[44] !== tabLabels.news) {
      t3 = {
        id: "news",
        label: tabLabels.news
      };
      $[44] = tabLabels.news;
      $[45] = t3;
    } else {
      t3 = $[45];
    }
    tabs.push(t2, t3);
    $[6] = competitionProfile?.features.matchStandings;
    $[7] = hasEvents;
    $[8] = hasLineups;
    $[9] = hasOdds;
    $[10] = isFinished;
    $[11] = isLive;
    $[12] = labels.matchPage.tabs;
    $[13] = tabs;
  } else {
    tabs = $[13];
  }
  const TAB_OPTIONS = tabs;
  const score = match.score;
  const statusCode = (match.status?.code || "") as string;
  let t2;
  if ($[46] !== labels.matchPage.status.aet || $[47] !== labels.matchPage.status.aetFull || $[48] !== labels.matchPage.status.ap || $[49] !== labels.matchPage.status.apFull || $[50] !== labels.matchPage.status.ft || $[51] !== labels.matchPage.status.ftFull || $[52] !== score || $[53] !== statusCode || $[54] !== winner) {
    t2 = computeScoreDisplay(score, statusCode, {
      ft: labels.matchPage.status.ft,
      ftFull: labels.matchPage.status.ftFull,
      aet: labels.matchPage.status.aet,
      aetFull: labels.matchPage.status.aetFull,
      ap: labels.matchPage.status.ap,
      apFull: labels.matchPage.status.apFull
    }, winner);
    $[46] = labels.matchPage.status.aet;
    $[47] = labels.matchPage.status.aetFull;
    $[48] = labels.matchPage.status.ap;
    $[49] = labels.matchPage.status.apFull;
    $[50] = labels.matchPage.status.ft;
    $[51] = labels.matchPage.status.ftFull;
    $[52] = score;
    $[53] = statusCode;
    $[54] = winner;
    $[55] = t2;
  } else {
    t2 = $[55];
  }
  const scoreDisplay = t2;
  const matchMinute = match.timing.currentMinute || "";
  let T0;
  let t10;
  let t11;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[56] !== awayTeam.assets?.logo || $[57] !== awayTeam.country?.code || $[58] !== awayTeam.country?.flag || $[59] !== awayTeam.id || $[60] !== awayTeam.metadata?.branding?.primaryColor || $[61] !== awayTeam.metadata?.branding?.secondaryColor || $[62] !== awayTeam.name || $[63] !== awayTeamNativeId || $[64] !== breadcrumbs || $[65] !== getTeamStanding || $[66] !== headerColors?.backgroundImageUrl || $[67] !== headerColors?.colors || $[68] !== homeTeam.assets?.logo || $[69] !== homeTeam.country?.code || $[70] !== homeTeam.country?.flag || $[71] !== homeTeam.id || $[72] !== homeTeam.metadata?.branding?.primaryColor || $[73] !== homeTeam.metadata?.branding?.secondaryColor || $[74] !== homeTeam.name || $[75] !== homeTeamNativeId || $[76] !== isFinished || $[77] !== isLive || $[78] !== labels.common || $[79] !== locale || $[80] !== match || $[81] !== matchMinute || $[82] !== nav || $[83] !== score?.competitorOne || $[84] !== score?.competitorTwo || $[85] !== scoreDisplay) {
    const homeTeamStanding = getTeamStanding(homeTeam.id);
    const awayTeamStanding = getTeamStanding(awayTeam.id);
    const homeTeamPosition = homeTeamStanding?.rank ?? null;
    const awayTeamPosition = awayTeamStanding?.rank ?? null;
    let t12;
    if ($[96] !== homeTeamNativeId || $[97] !== nav) {
      t12 = nav.team(homeTeamNativeId);
      $[96] = homeTeamNativeId;
      $[97] = nav;
      $[98] = t12;
    } else {
      t12 = $[98];
    }
    const homeTeamHref = t12;
    let t13;
    if ($[99] !== awayTeamNativeId || $[100] !== nav) {
      t13 = nav.team(awayTeamNativeId);
      $[99] = awayTeamNativeId;
      $[100] = nav;
      $[101] = t13;
    } else {
      t13 = $[101];
    }
    const awayTeamHref = t13;
    const kickoffDate = new Date(match.timing.kickoffTime);
    const formattedTime = kickoffDate.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
    t11 = "min-h-screen";
    T0 = BrandedMatchHeader;
    t5 = headerColors?.colors;
    t6 = headerColors?.backgroundImageUrl;
    t7 = breadcrumbs;
    if ($[102] !== match) {
      t8 = <MatchCompetitionInfo match={match} className="[color:var(--header-text-color-muted)]" />;
      $[102] = match;
      $[103] = t8;
    } else {
      t8 = $[103];
    }
    const t14 = homeTeam.assets?.logo || "";
    const t15 = homeTeam.assets?.logo;
    const t16 = homeTeam.country?.code;
    const t17 = homeTeam.country?.flag;
    const t18 = homeTeam.metadata?.branding?.primaryColor;
    const t19 = homeTeam.metadata?.branding?.secondaryColor;
    let t20;
    if ($[104] !== t15 || $[105] !== t16 || $[106] !== t17 || $[107] !== t18 || $[108] !== t19) {
      t20 = <TeamLogo src={t15} size="xl" countryCode={t16} countryFlag={t17} primaryColor={t18} secondaryColor={t19} />;
      $[104] = t15;
      $[105] = t16;
      $[106] = t17;
      $[107] = t18;
      $[108] = t19;
      $[109] = t20;
    } else {
      t20 = $[109];
    }
    const t21 = homeTeam.name;
    const t22 = formatPosition(homeTeamPosition, locale);
    if ($[110] !== homeTeam.name || $[111] !== homeTeamHref || $[112] !== t14 || $[113] !== t20 || $[114] !== t22) {
      t9 = {
        imageUrl: t14,
        image: t20,
        name: t21,
        subtitle: t22,
        href: homeTeamHref
      };
      $[110] = homeTeam.name;
      $[111] = homeTeamHref;
      $[112] = t14;
      $[113] = t20;
      $[114] = t22;
      $[115] = t9;
    } else {
      t9 = $[115];
    }
    const t23 = awayTeam.assets?.logo || "";
    const t24 = awayTeam.assets?.logo;
    const t25 = awayTeam.country?.code;
    const t26 = awayTeam.country?.flag;
    const t27 = awayTeam.metadata?.branding?.primaryColor;
    const t28 = awayTeam.metadata?.branding?.secondaryColor;
    let t29;
    if ($[116] !== t24 || $[117] !== t25 || $[118] !== t26 || $[119] !== t27 || $[120] !== t28) {
      t29 = <TeamLogo src={t24} size="xl" countryCode={t25} countryFlag={t26} primaryColor={t27} secondaryColor={t28} />;
      $[116] = t24;
      $[117] = t25;
      $[118] = t26;
      $[119] = t27;
      $[120] = t28;
      $[121] = t29;
    } else {
      t29 = $[121];
    }
    const t30 = awayTeam.name;
    const t31 = formatPosition(awayTeamPosition, locale);
    if ($[122] !== awayTeam.name || $[123] !== awayTeamHref || $[124] !== t23 || $[125] !== t29 || $[126] !== t31) {
      t10 = {
        imageUrl: t23,
        image: t29,
        name: t30,
        subtitle: t31,
        href: awayTeamHref
      };
      $[122] = awayTeam.name;
      $[123] = awayTeamHref;
      $[124] = t23;
      $[125] = t29;
      $[126] = t31;
      $[127] = t10;
    } else {
      t10 = $[127];
    }
    t3 = "text-center";
    t4 = !isLive && !isFinished ? <div className="mx-6"><div className="text-xl font-bold [color:var(--header-text-color)]">{formattedTime}</div><div className="text-sm [color:var(--header-text-color-muted)]">{getRelativeDate(kickoffDate, locale, labels.common.today, labels.common.tomorrow)}</div></div> : <div className="mx-6"><div className="text-3xl font-bold [color:var(--header-text-color)]">{isFinished ? `${scoreDisplay.scoreOne} - ${scoreDisplay.scoreTwo}` : `${score?.competitorOne || "0"} - ${score?.competitorTwo || "0"}`}</div><div className="text-xs [color:var(--header-text-color-muted)]">{isLive && `LIVE${matchMinute ? ` • ${matchMinute}'` : ""}`}{isFinished && <Tooltip content={scoreDisplay.statusTooltip} position="bottom"><span className="cursor-help">{scoreDisplay.statusLabel}</span></Tooltip>}</div></div>;
    $[56] = awayTeam.assets?.logo;
    $[57] = awayTeam.country?.code;
    $[58] = awayTeam.country?.flag;
    $[59] = awayTeam.id;
    $[60] = awayTeam.metadata?.branding?.primaryColor;
    $[61] = awayTeam.metadata?.branding?.secondaryColor;
    $[62] = awayTeam.name;
    $[63] = awayTeamNativeId;
    $[64] = breadcrumbs;
    $[65] = getTeamStanding;
    $[66] = headerColors?.backgroundImageUrl;
    $[67] = headerColors?.colors;
    $[68] = homeTeam.assets?.logo;
    $[69] = homeTeam.country?.code;
    $[70] = homeTeam.country?.flag;
    $[71] = homeTeam.id;
    $[72] = homeTeam.metadata?.branding?.primaryColor;
    $[73] = homeTeam.metadata?.branding?.secondaryColor;
    $[74] = homeTeam.name;
    $[75] = homeTeamNativeId;
    $[76] = isFinished;
    $[77] = isLive;
    $[78] = labels.common;
    $[79] = locale;
    $[80] = match;
    $[81] = matchMinute;
    $[82] = nav;
    $[83] = score?.competitorOne;
    $[84] = score?.competitorTwo;
    $[85] = scoreDisplay;
    $[86] = T0;
    $[87] = t10;
    $[88] = t11;
    $[89] = t3;
    $[90] = t4;
    $[91] = t5;
    $[92] = t6;
    $[93] = t7;
    $[94] = t8;
    $[95] = t9;
  } else {
    T0 = $[86];
    t10 = $[87];
    t11 = $[88];
    t3 = $[89];
    t4 = $[90];
    t5 = $[91];
    t6 = $[92];
    t7 = $[93];
    t8 = $[94];
    t9 = $[95];
  }
  let t12;
  if ($[128] !== t3 || $[129] !== t4) {
    t12 = <div className={t3}>{t4}</div>;
    $[128] = t3;
    $[129] = t4;
    $[130] = t12;
  } else {
    t12 = $[130];
  }
  let t13;
  if ($[131] !== T0 || $[132] !== t10 || $[133] !== t12 || $[134] !== t5 || $[135] !== t6 || $[136] !== t7 || $[137] !== t8 || $[138] !== t9) {
    t13 = <T0 colors={t5} backgroundImageUrl={t6} breadcrumbs={t7} topMeta={t8} leftTeam={t9} rightTeam={t10} centerContent={t12} />;
    $[131] = T0;
    $[132] = t10;
    $[133] = t12;
    $[134] = t5;
    $[135] = t6;
    $[136] = t7;
    $[137] = t8;
    $[138] = t9;
    $[139] = t13;
  } else {
    t13 = $[139];
  }
  let t14;
  if ($[140] !== TAB_OPTIONS || $[141] !== activeTab) {
    t14 = <PageNavigation options={TAB_OPTIONS} activeId={activeTab} onChange={setActiveTab} sticky={true} />;
    $[140] = TAB_OPTIONS;
    $[141] = activeTab;
    $[142] = t14;
  } else {
    t14 = $[142];
  }
  let t15;
  if ($[143] !== labels || $[144] !== tvListings) {
    t15 = <div className="max-w-350 mx-auto px-4 pt-6"><MatchBroadcastersRow resolved={tvListings} labels={labels} /></div>;
    $[143] = labels;
    $[144] = tvListings;
    $[145] = t15;
  } else {
    t15 = $[145];
  }
  let t16;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <AdSlot id="content-top" />;
    $[146] = t16;
  } else {
    t16 = $[146];
  }
  let t17;
  if ($[147] !== activeTab || $[148] !== awayTeam.assets?.logo || $[149] !== awayTeam.country?.code || $[150] !== awayTeam.country?.flag || $[151] !== awayTeam.metadata?.branding?.primaryColor || $[152] !== awayTeam.metadata?.branding?.secondaryColor || $[153] !== awayTeam.name || $[154] !== homeTeam.assets?.logo || $[155] !== homeTeam.country?.code || $[156] !== homeTeam.country?.flag || $[157] !== homeTeam.metadata?.branding?.primaryColor || $[158] !== homeTeam.metadata?.branding?.secondaryColor || $[159] !== homeTeam.name) {
    t17 = activeTab === "events" && <EventsTab homeTeamName={homeTeam.name} awayTeamName={awayTeam.name} homeTeamLogo={homeTeam.assets?.logo} awayTeamLogo={awayTeam.assets?.logo} homeTeamCountryCode={homeTeam.country?.code} homeTeamCountryFlag={homeTeam.country?.flag} homeTeamPrimaryColor={homeTeam.metadata?.branding?.primaryColor} homeTeamSecondaryColor={homeTeam.metadata?.branding?.secondaryColor} awayTeamCountryCode={awayTeam.country?.code} awayTeamCountryFlag={awayTeam.country?.flag} awayTeamPrimaryColor={awayTeam.metadata?.branding?.primaryColor} awayTeamSecondaryColor={awayTeam.metadata?.branding?.secondaryColor} />;
    $[147] = activeTab;
    $[148] = awayTeam.assets?.logo;
    $[149] = awayTeam.country?.code;
    $[150] = awayTeam.country?.flag;
    $[151] = awayTeam.metadata?.branding?.primaryColor;
    $[152] = awayTeam.metadata?.branding?.secondaryColor;
    $[153] = awayTeam.name;
    $[154] = homeTeam.assets?.logo;
    $[155] = homeTeam.country?.code;
    $[156] = homeTeam.country?.flag;
    $[157] = homeTeam.metadata?.branding?.primaryColor;
    $[158] = homeTeam.metadata?.branding?.secondaryColor;
    $[159] = homeTeam.name;
    $[160] = t17;
  } else {
    t17 = $[160];
  }
  let t18;
  if ($[161] !== activeTab || $[162] !== awayTeam.assets?.logo || $[163] !== awayTeam.country?.code || $[164] !== awayTeam.country?.flag || $[165] !== awayTeam.metadata?.branding?.primaryColor || $[166] !== awayTeam.metadata?.branding?.secondaryColor || $[167] !== awayTeam.name || $[168] !== awayTeam.threeLetterCode || $[169] !== homeTeam.assets?.logo || $[170] !== homeTeam.country?.code || $[171] !== homeTeam.country?.flag || $[172] !== homeTeam.metadata?.branding?.primaryColor || $[173] !== homeTeam.metadata?.branding?.secondaryColor || $[174] !== homeTeam.name || $[175] !== homeTeam.threeLetterCode) {
    t18 = activeTab === "lineups" && <LineupsTab homeTeamName={homeTeam.name} awayTeamName={awayTeam.name} homeTeamCode={homeTeam.threeLetterCode} awayTeamCode={awayTeam.threeLetterCode} homeTeamLogo={homeTeam.assets?.logo} awayTeamLogo={awayTeam.assets?.logo} homeTeamCountryCode={homeTeam.country?.code} homeTeamCountryFlag={homeTeam.country?.flag} homeTeamPrimaryColor={homeTeam.metadata?.branding?.primaryColor} homeTeamSecondaryColor={homeTeam.metadata?.branding?.secondaryColor} awayTeamCountryCode={awayTeam.country?.code} awayTeamCountryFlag={awayTeam.country?.flag} awayTeamPrimaryColor={awayTeam.metadata?.branding?.primaryColor} awayTeamSecondaryColor={awayTeam.metadata?.branding?.secondaryColor} />;
    $[161] = activeTab;
    $[162] = awayTeam.assets?.logo;
    $[163] = awayTeam.country?.code;
    $[164] = awayTeam.country?.flag;
    $[165] = awayTeam.metadata?.branding?.primaryColor;
    $[166] = awayTeam.metadata?.branding?.secondaryColor;
    $[167] = awayTeam.name;
    $[168] = awayTeam.threeLetterCode;
    $[169] = homeTeam.assets?.logo;
    $[170] = homeTeam.country?.code;
    $[171] = homeTeam.country?.flag;
    $[172] = homeTeam.metadata?.branding?.primaryColor;
    $[173] = homeTeam.metadata?.branding?.secondaryColor;
    $[174] = homeTeam.name;
    $[175] = homeTeam.threeLetterCode;
    $[176] = t18;
  } else {
    t18 = $[176];
  }
  let t19;
  if ($[177] !== activeTab || $[178] !== awayTeam.name || $[179] !== homeTeam.name || $[180] !== isLive || $[181] !== match.timing.kickoffTime || $[182] !== match.venue?.name || $[183] !== oddsLoading || $[184] !== oddsMap || $[185] !== refetchOdds) {
    t19 = activeTab === "betting" && <OddsTab homeTeamName={homeTeam.name} awayTeamName={awayTeam.name} kickoffTime={match.timing.kickoffTime} venue={match.venue?.name} oddsMap={oddsMap} loading={oddsLoading} onRefresh={refetchOdds} isLive={isLive} />;
    $[177] = activeTab;
    $[178] = awayTeam.name;
    $[179] = homeTeam.name;
    $[180] = isLive;
    $[181] = match.timing.kickoffTime;
    $[182] = match.venue?.name;
    $[183] = oddsLoading;
    $[184] = oddsMap;
    $[185] = refetchOdds;
    $[186] = t19;
  } else {
    t19 = $[186];
  }
  let t20;
  if ($[187] !== activeTab || $[188] !== awayTeam.assets?.logo || $[189] !== awayTeam.country?.code || $[190] !== awayTeam.country?.flag || $[191] !== awayTeam.metadata?.branding?.primaryColor || $[192] !== awayTeam.metadata?.branding?.secondaryColor || $[193] !== awayTeam.name || $[194] !== homeTeam.assets?.logo || $[195] !== homeTeam.country?.code || $[196] !== homeTeam.country?.flag || $[197] !== homeTeam.metadata?.branding?.primaryColor || $[198] !== homeTeam.metadata?.branding?.secondaryColor || $[199] !== homeTeam.name) {
    t20 = activeTab === "stats" && <StatsTab homeTeamName={homeTeam.name} awayTeamName={awayTeam.name} homeTeamLogo={homeTeam.assets?.logo} awayTeamLogo={awayTeam.assets?.logo} homeTeamColor={homeTeam.metadata?.branding?.primaryColor} awayTeamColor={awayTeam.metadata?.branding?.primaryColor} homeTeamCountryCode={homeTeam.country?.code} homeTeamCountryFlag={homeTeam.country?.flag} homeTeamPrimaryColor={homeTeam.metadata?.branding?.primaryColor} homeTeamSecondaryColor={homeTeam.metadata?.branding?.secondaryColor} awayTeamCountryCode={awayTeam.country?.code} awayTeamCountryFlag={awayTeam.country?.flag} awayTeamPrimaryColor={awayTeam.metadata?.branding?.primaryColor} awayTeamSecondaryColor={awayTeam.metadata?.branding?.secondaryColor} />;
    $[187] = activeTab;
    $[188] = awayTeam.assets?.logo;
    $[189] = awayTeam.country?.code;
    $[190] = awayTeam.country?.flag;
    $[191] = awayTeam.metadata?.branding?.primaryColor;
    $[192] = awayTeam.metadata?.branding?.secondaryColor;
    $[193] = awayTeam.name;
    $[194] = homeTeam.assets?.logo;
    $[195] = homeTeam.country?.code;
    $[196] = homeTeam.country?.flag;
    $[197] = homeTeam.metadata?.branding?.primaryColor;
    $[198] = homeTeam.metadata?.branding?.secondaryColor;
    $[199] = homeTeam.name;
    $[200] = t20;
  } else {
    t20 = $[200];
  }
  let t21;
  if ($[201] !== activeTab || $[202] !== awayTeam || $[203] !== homeTeam || $[204] !== oddsMap) {
    t21 = activeTab === "form" && <FormTab homeTeamId={homeTeam.id} awayTeamId={awayTeam.id} homeTeam={homeTeam} awayTeam={awayTeam} oddsMap={oddsMap} />;
    $[201] = activeTab;
    $[202] = awayTeam;
    $[203] = homeTeam;
    $[204] = oddsMap;
    $[205] = t21;
  } else {
    t21 = $[205];
  }
  let t22;
  if ($[206] !== activeTab || $[207] !== awayTeam.id || $[208] !== homeTeam.id) {
    t22 = activeTab === "standings" && <StandingsTab homeTeamId={homeTeam.id} awayTeamId={awayTeam.id} />;
    $[206] = activeTab;
    $[207] = awayTeam.id;
    $[208] = homeTeam.id;
    $[209] = t22;
  } else {
    t22 = $[209];
  }
  let t23;
  if ($[210] !== activeTab || $[211] !== awayTeam.id || $[212] !== awayTeam.metadata?.branding?.primaryColor || $[213] !== homeTeam.id || $[214] !== homeTeam.metadata?.branding?.primaryColor || $[215] !== oddsMap) {
    t23 = activeTab === "h2h" && <H2HTab homeTeamId={homeTeam.id} awayTeamId={awayTeam.id} homeTeamColor={homeTeam.metadata?.branding?.primaryColor} awayTeamColor={awayTeam.metadata?.branding?.primaryColor} oddsMap={oddsMap} />;
    $[210] = activeTab;
    $[211] = awayTeam.id;
    $[212] = awayTeam.metadata?.branding?.primaryColor;
    $[213] = homeTeam.id;
    $[214] = homeTeam.metadata?.branding?.primaryColor;
    $[215] = oddsMap;
    $[216] = t23;
  } else {
    t23 = $[216];
  }
  let t24;
  if ($[217] !== activeTab || $[218] !== awayTeam.name || $[219] !== homeTeam.name) {
    t24 = activeTab === "news" && <NewsTab homeTeamName={homeTeam.name} awayTeamName={awayTeam.name} />;
    $[217] = activeTab;
    $[218] = awayTeam.name;
    $[219] = homeTeam.name;
    $[220] = t24;
  } else {
    t24 = $[220];
  }
  let t25;
  if ($[221] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <AdSlot id="content-bottom" />;
    $[221] = t25;
  } else {
    t25 = $[221];
  }
  let t26;
  if ($[222] !== t17 || $[223] !== t18 || $[224] !== t19 || $[225] !== t20 || $[226] !== t21 || $[227] !== t22 || $[228] !== t23 || $[229] !== t24) {
    t26 = <div className="max-w-350 mx-auto px-4 py-8 space-y-8">{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}</div>;
    $[222] = t17;
    $[223] = t18;
    $[224] = t19;
    $[225] = t20;
    $[226] = t21;
    $[227] = t22;
    $[228] = t23;
    $[229] = t24;
    $[230] = t26;
  } else {
    t26 = $[230];
  }
  let t27;
  if ($[231] !== t11 || $[232] !== t13 || $[233] !== t14 || $[234] !== t15 || $[235] !== t26) {
    t27 = <div className={t11}>{t13}{t14}{t15}{t26}</div>;
    $[231] = t11;
    $[232] = t13;
    $[233] = t14;
    $[234] = t15;
    $[235] = t26;
    $[236] = t27;
  } else {
    t27 = $[236];
  }
  return t27;
}